// A small note view of a song drawn on a canvas: its four sections side by side (pattern letters
// on top), drums along the bottom, bass / harmony / lead / classic as coloured bars by pitch. Purely visual;
// the card's text carries the information.
import { useEffect, useRef } from "react";
import {
  laneSlot,
  PATTERN_NAMES,
  sections,
  SONG_BARS,
  SONG_TICKS,
  songEvents,
  type LoopData,
  isAtmosPad,
  isInstrumentSlot,
  FIRST_ATMOS,
} from "@eb/shared";

// bass, harmony, lead, classic (Okabe–Ito), atmos (lavender)
const ROLE_COLOURS = ["#e69f00", "#56b4e9", "#cc79a7", "#f0e442", "#9b8cf2"];
const DRUM_COLOUR = "#009e73";

export function drawMiniNotes(
  ctx: CanvasRenderingContext2D,
  loop: LoopData | undefined,
  w: number,
  h: number,
  grid: string,
) {
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = grid;
  for (let bar = 8; bar < SONG_BARS; bar += 8) ctx.fillRect(Math.round((bar / SONG_BARS) * w), 0, 1, h);
  if (!loop) return;
  const events = songEvents(loop);
  const secs = sections(loop);
  // Pattern letters when the song has more than one pattern.
  if (secs.some((p) => p !== 0) && w >= 160) {
    ctx.font = "600 9px system-ui, sans-serif";
    ctx.fillStyle = "#cfd6e4";
    ctx.globalAlpha = 0.8;
    secs.forEach((p, k) => ctx.fillText(PATTERN_NAMES[p], (k * w) / secs.length + 3, 9));
    ctx.globalAlpha = 1;
  }
  const drumH = Math.max(6, Math.round(h * 0.28));
  const inst = events.filter((e) => isInstrumentSlot(e.pad));
  let lo = 127;
  let hi = 0;
  for (const e of inst) {
    lo = Math.min(lo, e.note);
    hi = Math.max(hi, e.note);
  }
  const span = Math.max(12, hi - lo + 1);
  const top = 2;
  const melH = h - drumH - top - 3;
  for (const e of events) {
    const x = (e.tick / SONG_TICKS) * w;
    if (e.pad < 16) {
      ctx.fillStyle = DRUM_COLOUR;
      ctx.globalAlpha = 0.35 + (e.velocity / 127) * 0.65;
      const row = e.pad % 4;
      ctx.fillRect(x, h - drumH + (row * drumH) / 4, 2, Math.max(1, drumH / 4 - 1));
    } else {
      if (isAtmosPad(e.pad)) {
        // The atmosphere: a soft band along the top.
        ctx.fillStyle = "#9b8cf2";
        ctx.globalAlpha = 0.35;
        ctx.fillRect(x, top + (e.pad - FIRST_ATMOS) * 0.4, Math.max(1, (e.length_ticks / SONG_TICKS) * w), 2);
        continue;
      }
      ctx.fillStyle = ROLE_COLOURS[laneSlot(e.pad) - 16];
      ctx.globalAlpha = 0.9;
      const y = top + melH - ((e.note - lo + 0.5) / span) * melH;
      ctx.fillRect(x, y - 1.5, Math.max(1, (e.length_ticks / SONG_TICKS) * w), 3);
    }
  }
  ctx.globalAlpha = 1;
}

export function MiniNotes({
  loop,
  width = 260,
  height = 56,
  label,
  className = "mini-notes",
  style,
}: {
  loop: LoopData | undefined;
  width?: number;
  height?: number;
  label: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    c.width = width * dpr;
    c.height = height * dpr;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawMiniNotes(ctx, loop, width, height, "rgba(255,255,255,0.07)");
  }, [loop, width, height]);
  return <canvas ref={ref} className={className} style={{ ...style, width, height }} role="img" aria-label={label} />;
}
